import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { colors, radius } from '../theme';

// Sumber "Dari video panjang" di form Shorts (2026-10-26). Pilih video panjang yang filenya masih ada → saran potongan
// otomatis (bagian paling hidup dari audio, biasanya reff) atau atur sendiri lewat pratinjau → klip vertikal dibuat di
// server → onKlip({ id, file, judul, ... }) lalu form Shorts berjalan seperti upload file biasa. Backend: routes/repurpose.js.
const API = 'https://api.starhubcreative.id';
const DURASI = [15, 30, 45, 57];
const JENIS = { kompilasi: 'Kompilasi', video_musik: 'Video musik', narasi: 'Video narasi', video_jadi: 'Video jadi' };
const mmss = s => { const n = Math.max(0, Math.round(Number(s) || 0)); const h = Math.floor(n / 3600), m = Math.floor((n % 3600) / 60), d = n % 60; return h ? `${h}:${String(m).padStart(2, '0')}:${String(d).padStart(2, '0')}` : `${m}:${String(d).padStart(2, '0')}`; };
const tgl = iso => new Date(iso).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' });
const token = () => { try { return localStorage.getItem('sm_token') || ''; } catch (e) { return ''; } };
const tombol = aktif => ({ padding: '6px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: `1px solid ${aktif ? colors.accent : colors.border}`, background: aktif ? colors.accentMuted : colors.bgCard, color: aktif ? colors.accentText : colors.textSecondary, display: 'inline-flex', alignItems: 'center', gap: 5 });

export default function DariVideoPanjang({ onKlip, onBatal }) {
  const [list, setList] = useState(null), [pilih, setPilih] = useState(null);
  const [durasi, setDurasi] = useState(45), [format, setFormat] = useState('blur'), [start, setStart] = useState(0);
  const [saran, setSaran] = useState(null), [klip, setKlip] = useState(null), [err, setErr] = useState('');
  const vidRef = useRef(null), henti = useRef(false);
  useEffect(() => {
    axios.get(`${API}/api/repurpose/sumber`).then(r => setList(r.data || [])).catch(() => { setList([]); setErr('Daftar video panjang tidak bisa dimuat'); });
    return () => { henti.current = true; };
  }, []);

  // Saran potongan: dianalisa di server (video 1 jam ±1–2 menit pertama kali, berikutnya instan).
  useEffect(() => {
    if (!pilih) return undefined;
    let batal = false;
    setSaran({ status: 'proses' });
    (async () => {
      for (let i = 0; i < 200 && !batal && !henti.current; i++) {
        try {
          const { data } = await axios.get(`${API}/api/repurpose/${pilih.id}/saran`, { params: { durasi } });
          if (batal) return;
          if (data.status === 'siap') {
            setSaran(data);
            if (data.saran && data.saran.length) {
              const top = data.saran.reduce((a, b) => (b.skor > a.skor ? b : a));
              setStart(top.start);
            }
            return;
          }
          if (data.status === 'gagal') { setSaran(data); return; }
          setSaran(data);
        } catch (e) { if (!batal) setSaran({ status: 'gagal', error: e.response?.data?.error || 'Analisa gagal. Pilih posisi manual.' }); return; }
        await new Promise(r => setTimeout(r, 2500));
      }
    })();
    return () => { batal = true; };
  }, [pilih, durasi]);

  useEffect(() => { const v = vidRef.current; if (v && Math.abs(v.currentTime - start) > 0.5) { try { v.currentTime = start; } catch (e) { /* belum dimuat */ } } }, [start]);

  const maks = pilih ? Math.max(0, (Number(pilih.durasi) || (saran && saran.durasi_video) || 0) - durasi) : 0;
  const geser = d => setStart(s => Math.min(maks, Math.max(0, Math.round(s + d))));

  const buat = async () => {
    setErr(''); setKlip({ status: 'antre' });
    try {
      const { data } = await axios.post(`${API}/api/repurpose/klip`, { job_id: pilih.id, start, durasi, format });
      for (;;) {
        await new Promise(r => setTimeout(r, 2000));
        if (henti.current) return;
        const { data: k } = await axios.get(`${API}/api/repurpose/klip/${data.id}`);
        if (k.status === 'gagal') { setKlip(null); setErr(k.error || 'Gagal membuat klip'); return; }
        if (k.status === 'siap') {
          setKlip({ status: 'unduh' });
          const r = await axios.get(`${API}/api/repurpose/klip/${data.id}/file`, { responseType: 'blob', timeout: 0 });
          const file = new File([r.data], `klip-${mmss(start).replace(/:/g, '.')}.mp4`, { type: 'video/mp4' });
          const ytId = (pilih.channels || []).find(c => c.youtube_video_id)?.youtube_video_id;
          setKlip(null);
          onKlip({ id: data.id, file, judul: pilih.title, linkLengkap: ytId ? `https://youtu.be/${ytId}` : null,
            channelIds: (pilih.channels || []).map(c => c.channel_id), seoLang: pilih.seo_lang, label: `${pilih.title} · ${mmss(start)}–${mmss(start + durasi)}` });
          return;
        }
        setKlip(k);
      }
    } catch (e) { setKlip(null); setErr(e.response?.data?.error || 'Gagal membuat klip'); }
  };

  if (list === null) return <div style={{ fontSize: 13, color: colors.textSecondary, padding: 12 }}>Memuat video panjang…</div>;
  if (!pilih) return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 8 }}>
        Pilih video panjang kamu (kompilasi, video musik, narasi, video jadi). File video panjang disimpan <b>7 hari setelah diupload</b>, jadi yang lebih lama tidak muncul di sini.
      </div>
      {err && <div style={{ fontSize: 12, color: colors.danger, marginBottom: 6 }}>{err}</div>}
      {list.length === 0 ? (
        <div style={{ border: `1px dashed ${colors.border}`, borderRadius: 8, padding: 16, fontSize: 13, color: colors.textSecondary, textAlign: 'center' }}>
          Belum ada video panjang yang filenya masih tersimpan. Buat dulu di menu <b>Video panjang</b>.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 6, maxHeight: 320, overflowY: 'auto' }}>
          {list.map(v => (
            <div key={v.id} onClick={() => { setPilih(v); setStart(0); }} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: 8, borderRadius: 8, border: `1px solid ${colors.border}`, cursor: 'pointer', background: colors.bgCard }}>
              {v.thumbnail_url ? <img src={`${API}${v.thumbnail_url}`} alt="" style={{ width: 88, aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: 6, flex: '0 0 auto', background: colors.bgCardMuted }} />
                : <div style={{ width: 88, aspectRatio: '16 / 9', borderRadius: 6, background: colors.bgCardMuted, flex: '0 0 auto' }} />}
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.title || '(tanpa judul)'}</div>
                <div style={{ fontSize: 11, color: colors.textSecondary }}>{JENIS[v.jenis] || v.jenis} · {mmss(v.durasi)}{v.seo_lang ? ` · ${v.seo_lang.toUpperCase()}` : ''} · dihapus ±{tgl(v.hapus_pada)}</div>
              </div>
              <i className="ti ti-chevron-right" style={{ color: colors.textSecondary }} />
            </div>
          ))}
        </div>
      )}
      {onBatal && <button type="button" onClick={onBatal} style={{ ...tombol(false), marginTop: 8 }}>Batal</button>}
    </div>
  );

  const sedang = !!klip;
  return (
    <div style={{ marginBottom: 12, display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>🎬 {pilih.title}</div>
        <button type="button" disabled={sedang} onClick={() => { setPilih(null); setSaran(null); }} style={tombol(false)}>Ganti video</button>
      </div>
      <video ref={vidRef} src={`${API}/api/ugc/preview/${pilih.id}?token=${token()}`} controls preload="metadata"
        onLoadedMetadata={e => { try { e.currentTarget.currentTime = start; } catch (x) { /* abaikan */ } }}
        style={{ width: '100%', maxHeight: 220, borderRadius: 8, background: '#000' }} />
      <div>
        <div style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 4 }}>Durasi Short</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {DURASI.map(d => <button key={d} type="button" disabled={sedang} onClick={() => setDurasi(d)} style={tombol(durasi === d)}>{d} detik</button>)}
        </div>
      </div>
      <div>
        <div style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 4 }}>Bagian yang dipotong</div>
        {saran && ['proses', 'antre'].includes(saran.status) && <div style={{ fontSize: 12, color: colors.textSecondary }}><i className="ti ti-loader-2" /> Mencari bagian paling hidup (reff)… video panjang butuh ±1–2 menit pertama kali. Sambil menunggu, bisa pilih posisi sendiri.</div>}
        {saran && saran.status === 'gagal' && <div style={{ fontSize: 12, color: colors.danger }}>{saran.error}</div>}
        {saran && saran.status === 'siap' && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
            {saran.saran.map((x, i) => (
              <button key={x.start} type="button" disabled={sedang} onClick={() => setStart(x.start)} style={tombol(Math.abs(start - x.start) < 1)} title="Saran dari energi audio (biasanya reff)">
                <i className="ti ti-sparkles" />Saran {i + 1} · {mmss(x.start)}{x.skor === 100 ? ' · paling hidup' : ''}
              </button>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" disabled={sedang} onClick={() => geser(-5)} style={tombol(false)}>−5 dtk</button>
          <span style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary, minWidth: 110, textAlign: 'center' }}>{mmss(start)} – {mmss(start + durasi)}</span>
          <button type="button" disabled={sedang} onClick={() => geser(5)} style={tombol(false)}>+5 dtk</button>
          <button type="button" disabled={sedang} onClick={() => vidRef.current && setStart(Math.min(maks, Math.round(vidRef.current.currentTime)))} style={tombol(false)}><i className="ti ti-player-play" />Mulai dari posisi video sekarang</button>
          <button type="button" onClick={() => { const v = vidRef.current; if (v) { v.currentTime = start; v.play().catch(() => {}); } }} style={tombol(false)}><i className="ti ti-eye" />Putar bagian ini</button>
        </div>
      </div>
      <div>
        <div style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 4 }}>Tampilan vertikal</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button type="button" disabled={sedang} onClick={() => setFormat('blur')} style={tombol(format === 'blur')}>Video utuh + latar blur</button>
          <button type="button" disabled={sedang} onClick={() => setFormat('isi')} style={tombol(format === 'isi')}>Penuh layar (sisi kiri-kanan terpotong)</button>
        </div>
        <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 4 }}>Untuk video berlirik, pilih "Video utuh" supaya teks lirik tidak terpotong.</div>
      </div>
      {err && <div style={{ fontSize: 12, color: colors.danger }}>{err}</div>}
      <button type="button" disabled={sedang} onClick={buat} style={{ ...tombol(true), justifyContent: 'center', padding: '9px 12px', fontSize: 13, background: colors.accent, color: '#fff', borderColor: colors.accent }}>
        <i className={`ti ${sedang ? 'ti-loader-2' : 'ti-scissors'}`} />
        {!klip ? 'Buat klip Short' : klip.status === 'unduh' ? 'Menyiapkan pratinjau…' : klip.status === 'proses' ? 'Membuat klip… (±20–40 detik)' : `Antre${klip.antre_ke ? ` ke-${klip.antre_ke}` : ''}…`}
      </button>
    </div>
  );
}
